import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { util as sdkUtil } from '../util/sdkLoader';
import { denormalisedResponseEntities } from '../util/data';
import * as log from '../util/log';
import { registerPushIfNativeApp } from '../util/nativeApp';
import { storableError } from '../util/errors';
import { isUserAuthorized } from '../util/userHelpers';
import {
  getStatesNeedingProviderAttention,
  getStatesNeedingCustomerAttention,
} from '../transactions/transaction';

import { authInfo } from './auth.duck';
import { updateStripeConnectAccount } from './stripeConnectAccount.duck';

// ================ Helper Functions ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

// ================ Async Thunks ================ //

//////////////////////////////////////////////////////////////////////
// Fetch ownListings to check if currentUser has published listings //
//////////////////////////////////////////////////////////////////////

const fetchCurrentUserHasListingsPayloadCreator = (_, thunkAPI) => {
  const { extra: sdk, rejectWithValue } = thunkAPI;

  // Note: we don't gate on getState().user.currentUser here. This thunk is
  // often dispatched immediately after fetchCurrentUser's payload creator
  // resolves, before its .fulfilled reducer has committed the user into
  // state — a race that used to leave currentUserHasListings stuck at
  // false. The SDK call itself authenticates via the session cookie, so
  // it already knows who the caller is.
  const params = { page: 1, perPage: 1 };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = !!(response?.data?.data && response.data.data.length > 0);
      return { hasListings };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasListingsThunk = createAsyncThunk(
  'user/fetchCurrentUserHasListings',
  fetchCurrentUserHasListingsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasListingsThunk()).unwrap();
};

///////////////////////////////////////////////////////////
// Fetch transactions to check if currentUser has orders //
///////////////////////////////////////////////////////////

const fetchCurrentUserHasOrdersPayloadCreator = (_, { getState, extra: sdk, rejectWithValue }) => {
  if (!getState().user.currentUser) {
    return Promise.resolve({ hasOrders: false });
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      return { hasOrders: !!hasOrders };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasOrdersThunk = createAsyncThunk(
  'user/fetchCurrentUserHasOrders',
  fetchCurrentUserHasOrdersPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasOrdersThunk()).unwrap();
};

/////////////////////////////////////////////////////////////////////////////////////
// Fetch transactions in specific states to check if currentUser has notifications //
/////////////////////////////////////////////////////////////////////////////////////

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

const fetchCurrentUserNotificationsPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  const statesNeedingProviderAttention = getStatesNeedingProviderAttention() || [];
  const statesNeedingCustomerAttention = getStatesNeedingCustomerAttention() || [];

  const paramsForSales = {
    only: 'sale',
    states: statesNeedingProviderAttention.map(state => `state/${state}`).join(','),
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };
  const paramsForOrders = {
    only: 'order',
    states: statesNeedingCustomerAttention.map(state => `state/${state}`).join(','),
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  return Promise.all([
    sdk.transactions.query(paramsForSales),
    sdk.transactions.query(paramsForOrders),
  ])
    .then(([sales, orders]) => {
      const saleNotificationsCount = sales.data.data.length;
      const orderNotificationsCount = orders.data.data.length;
      return { saleNotificationsCount, orderNotificationsCount };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserNotificationsThunk = createAsyncThunk(
  'user/fetchCurrentUserNotifications',
  fetchCurrentUserNotificationsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserNotificationsThunk()).unwrap();
};

// ================ Unread messages ================ //

// Cap on how many recent transactions we inspect when computing the
// unread-messages count. Each transaction below costs one extra
// messages.query call, so we parallelize but keep the limit tight.
const UNREAD_SCAN_LIMIT = 50;

export const fetchUnreadMessagesThunk = createAsyncThunk(
  'user/fetchUnreadMessages',
  async (_, { getState, extra: sdk, rejectWithValue }) => {
    const currentUser = getState().user?.currentUser;
    const currentUserUuid = currentUser?.id?.uuid;
    const empty = { all: [], orders: [], sales: [] };
    if (!currentUserUuid) {
      return empty;
    }

    const lastReadAt = currentUser?.attributes?.profile?.privateData?.lastReadAt || {};

    try {
      // Include the listing relationship (with `deleted` field) so we
      // can skip transactions whose listing has been removed — those
      // threads are no longer actionable, and counting them in the
      // unread badge produces an "inbox is empty but badge says 1"
      // ghost-notification UX.
      const txRes = await sdk.transactions.query({
        page: 1,
        perPage: UNREAD_SCAN_LIMIT,
        'fields.transaction': ['lastTransition', 'lastTransitionedAt'],
        include: ['listing'],
        'fields.listing': ['deleted'],
      });
      const txs = txRes?.data?.data || [];
      const included = txRes?.data?.included || [];

      // Build a quick lookup: listingId → deleted? for the included
      // listings. Anything not in the map is treated as "listing
      // missing" → also skip (defensive — orphaned threads with no
      // listing relationship at all should not count either).
      const deletedByListingId = new Map();
      included
        .filter(i => i.type === 'listing')
        .forEach(l => deletedByListingId.set(l.id.uuid, !!l.attributes?.deleted));

      // Per-tx: determine role + whether there's an unread message.
      // Role from relationships (always returned alongside data, even
      // with sparse `fields.transaction`); unread via latest-message
      // scan against the user's privateData.lastReadAt record.
      // Pre-filter out transactions on deleted/missing listings.
      const actionableTxs = txs.filter(tx => {
        const listingId = tx.relationships?.listing?.data?.id?.uuid;
        if (!listingId) return false; // no listing rel → orphan thread
        const isDeleted = deletedByListingId.get(listingId);
        // If the listing isn't in `included` it was filtered out by
        // the API (typically because it's deleted). Treat unknown as
        // deleted; we only count transactions with a confirmed-live
        // listing.
        return isDeleted === false;
      });

      const perTx = await Promise.all(
        actionableTxs.map(async tx => {
          const txId = tx.id.uuid;
          const customerUuid = tx.relationships?.customer?.data?.id?.uuid;
          const role = customerUuid === currentUserUuid ? 'customer' : 'provider';
          try {
            const msgRes = await sdk.messages.query({
              transactionId: tx.id,
              page: 1,
              perPage: 1,
              'fields.message': ['createdAt'],
              include: ['sender'],
            });
            const latest = msgRes?.data?.data?.[0];
            if (!latest) return null;
            const senderUuid = latest.relationships?.sender?.data?.id?.uuid;
            if (!senderUuid || senderUuid === currentUserUuid) return null;
            const createdAtMs = new Date(latest.attributes.createdAt).getTime();
            const readAtMs = lastReadAt[txId]
              ? new Date(lastReadAt[txId]).getTime()
              : 0;
            return createdAtMs > readAtMs ? { txId, role } : null;
          } catch (e) {
            console.warn('Unread scan: message query failed for', txId, e);
            return null;
          }
        })
      );

      const unread = perTx.filter(Boolean);
      return {
        all: unread.map(u => u.txId),
        orders: unread.filter(u => u.role === 'customer').map(u => u.txId),
        sales: unread.filter(u => u.role === 'provider').map(u => u.txId),
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const fetchUnreadMessages = () => dispatch =>
  dispatch(fetchUnreadMessagesThunk()).unwrap();

export const markTransactionRead = txId => async (dispatch, getState, sdk) => {
  if (!txId) return;
  // Optimistically drop the tx from the unread list so the badge updates
  // immediately; then persist the read timestamp to privateData.
  dispatch(markTransactionReadLocal(txId));
  try {
    const currentUser = getState().user?.currentUser;
    const existing =
      currentUser?.attributes?.profile?.privateData?.lastReadAt || {};
    const next = { ...existing, [txId]: new Date().toISOString() };
    const res = await sdk.currentUser.updateProfile(
      { privateData: { lastReadAt: next } },
      { expand: true }
    );
    const updated = res?.data?.data;
    if (updated) {
      dispatch(setCurrentUser(updated));
    }
  } catch (e) {
    console.warn('markTransactionRead failed:', e);
  }
};

const fetchCurrentUserPayloadCreator = (options, thunkAPI) => {
  const { getState, dispatch, extra: sdk, rejectWithValue } = thunkAPI;
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve(state.user.currentUser);
  }

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    return Promise.resolve(null);
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(updateStripeConnectAccount(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);

      // Inside the native app shell: register this device for push
      // notifications and persist the token on privateData. No-op on the
      // plain website and during SSR (see util/nativeApp.js).
      registerPushIfNativeApp(sdk, currentUser);

      return currentUser;
    })
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (updateHasListings !== false) {
          dispatch(fetchCurrentUserHasListings());
        }

        if (updateNotifications !== false) {
          dispatch(fetchCurrentUserNotifications());
          dispatch(fetchUnreadMessages());
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      return rejectWithValue(storableError(e));
    });
};

export const fetchCurrentUserThunk = createAsyncThunk(
  'user/fetchCurrentUser',
  fetchCurrentUserPayloadCreator
);
// Backward compatible wrapper for the thunk
/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserThunk(options)).unwrap();
};

/////////////////////////////////////////////
// Send verification email to currentUser //
/////////////////////////////////////////////

const sendVerificationEmailPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => ({}))
    .catch(e => rejectWithValue(storableError(e)));
};
export const sendVerificationEmailThunk = createAsyncThunk(
  'user/sendVerificationEmail',
  sendVerificationEmailPayloadCreator,
  {
    condition: (_, { getState }) => {
      return !getState()?.user?.sendVerificationEmailInProgress;
    },
  }
);

// Backward compatible wrapper for the thunk
export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  return dispatch(sendVerificationEmailThunk()).unwrap();
};

// ================ Slice ================ //

const userSlice = createSlice({
  name: 'user',
  initialState: {
    currentUser: null,
    currentUserShowTimestamp: 0,
    currentUserShowError: null,
    currentUserHasListings: false,
    // Distinguishes "no listings" from "listings check hasn't resolved yet".
    // The dashboard mode-redirect must not treat the brief pre-resolution
    // window (where a real host momentarily reads as a non-host) as a true
    // renter — see ProviderDashboardPage's redirect effect.
    currentUserHasListingsLoaded: false,
    currentUserHasListingsError: null,
    currentUserSaleNotificationCount: 0,
    currentUserOrderNotificationCount: 0,
    currentUserNotificationCountError: null,
    currentUserHasOrders: null, // This is not fetched unless unverified emails exist
    currentUserHasOrdersError: null,
    sendVerificationEmailInProgress: false,
    sendVerificationEmailError: null,
    // Unread-message tracking: list of transaction UUIDs where the latest
    // message is newer than the current user's privateData.lastReadAt[txId]
    // and not authored by the current user. Used for the Messages badge
    // and per-row dots in the inbox.
    //
    // `unreadMessageTxIds` — flat, used by the sidebar Messages badge
    //   and InboxPage row dots (role-agnostic).
    // `unreadOrderTxIds` / `unreadSaleTxIds` — role-split, used by the
    //   InboxPage tab-label badges so users can see which tab has
    //   unreads without hunting.
    unreadMessageTxIds: [],
    unreadOrderTxIds: [],
    unreadSaleTxIds: [],
  },
  reducers: {
    clearCurrentUser: state => {
      state.currentUser = null;
      state.currentUserShowError = null;
      state.currentUserHasListings = false;
      state.currentUserHasListingsLoaded = false;
      state.currentUserHasListingsError = null;
      state.currentUserSaleNotificationCount = 0;
      state.currentUserOrderNotificationCount = 0;

      state.currentUserNotificationCountError = null;
      state.unreadMessageTxIds = [];
      state.unreadOrderTxIds = [];
      state.unreadSaleTxIds = [];
    },
    setCurrentUser: (state, action) => {
      state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
    },
    setCurrentUserHasOrders: state => {
      state.currentUserHasOrders = true;
    },
    setUnreadMessageTxIds: (state, action) => {
      state.unreadMessageTxIds = action.payload || [];
    },
    markTransactionReadLocal: (state, action) => {
      const txId = action.payload;
      state.unreadMessageTxIds = state.unreadMessageTxIds.filter(id => id !== txId);
      state.unreadOrderTxIds = state.unreadOrderTxIds.filter(id => id !== txId);
      state.unreadSaleTxIds = state.unreadSaleTxIds.filter(id => id !== txId);
    },
  },
  extraReducers: builder => {
    builder
      // fetchCurrentUser
      .addCase(fetchCurrentUserThunk.pending, state => {
        state.currentUserShowError = null;
      })
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
        state.currentUserShowTimestamp = action.payload ? new Date().getTime() : 0;
      })
      .addCase(fetchCurrentUserThunk.rejected, (state, action) => {
        // eslint-disable-next-line no-console
        console.error(action.payload);
        state.currentUserShowError = action.payload;
      })
      // fetchCurrentUserHasListings
      .addCase(fetchCurrentUserHasListingsThunk.pending, state => {
        state.currentUserHasListingsError = null;
      })
      .addCase(fetchCurrentUserHasListingsThunk.fulfilled, (state, action) => {
        state.currentUserHasListings = action.payload.hasListings;
        // Mark resolved only on success — a failed check (e.g. a 429) leaves
        // this false so the dashboard redirect stays inert rather than acting
        // on an unknown host/renter status.
        state.currentUserHasListingsLoaded = true;
      })
      .addCase(fetchCurrentUserHasListingsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserHasListingsError = action.payload;
      })
      // fetchCurrentUserNotifications
      .addCase(fetchCurrentUserNotificationsThunk.pending, state => {
        state.currentUserNotificationCountError = null;
      })
      .addCase(fetchCurrentUserNotificationsThunk.fulfilled, (state, action) => {
        state.currentUserSaleNotificationCount = action.payload.saleNotificationsCount;
        state.currentUserOrderNotificationCount = action.payload.orderNotificationsCount;
      })
      .addCase(fetchCurrentUserNotificationsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserNotificationCountError = action.payload;
      })
      // fetchUnreadMessages
      .addCase(fetchUnreadMessagesThunk.fulfilled, (state, action) => {
        const payload = action.payload || { all: [], orders: [], sales: [] };
        state.unreadMessageTxIds = payload.all || [];
        state.unreadOrderTxIds = payload.orders || [];
        state.unreadSaleTxIds = payload.sales || [];
      })
      .addCase(fetchUnreadMessagesThunk.rejected, (state, action) => {
        console.warn('fetchUnreadMessages rejected:', action.payload); // eslint-disable-line
      })
      // fetchCurrentUserHasOrders
      .addCase(fetchCurrentUserHasOrdersThunk.pending, state => {
        state.currentUserHasOrdersError = null;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.fulfilled, (state, action) => {
        state.currentUserHasOrders = action.payload.hasOrders;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserHasOrdersError = action.payload;
      })
      // sendVerificationEmail
      .addCase(sendVerificationEmailThunk.pending, state => {
        state.sendVerificationEmailInProgress = true;
        state.sendVerificationEmailError = null;
      })
      .addCase(sendVerificationEmailThunk.fulfilled, state => {
        state.sendVerificationEmailInProgress = false;
      })
      .addCase(sendVerificationEmailThunk.rejected, (state, action) => {
        state.sendVerificationEmailInProgress = false;
        state.sendVerificationEmailError = action.payload;
      });
  },
});

export default userSlice.reducer;

export const {
  clearCurrentUser,
  setCurrentUser,
  setCurrentUserHasOrders,
  setUnreadMessageTxIds,
  markTransactionReadLocal,
} = userSlice.actions;

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};
